// src/pages/Courses.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  BookOpen,
  Clock,
  CheckCircle2,
  AlertCircle,
  Search,
  ArrowRight,
} from "lucide-react";

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

async function fetchCourses() {
  const res = await fetch(`${API_BASE}/api/content/courses`, {
    credentials: "include",
  });
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const json = isJson ? await res.json().catch(() => ({})) : null;
  if (!res.ok || json?.ok === false) {
    const msg = json?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return Array.isArray(json?.data) ? json.data : [];
}

/**
 * Brand-aligned “fun rainbow”:
 * - Every gradient is anchored with ORANGE on one side
 * - Secondary hue adds variety while staying premium
 */
const PALETTE = {
  discover: {
    color: "from-orange-600 to-amber-500",
    chipBg: "bg-orange-50 dark:bg-orange-500/10",
    chipText: "text-orange-700 dark:text-orange-200",
  },
  ugunduzi: {
    color: "from-orange-600 to-emerald-500",
    chipBg: "bg-emerald-50 dark:bg-emerald-500/10",
    chipText: "text-emerald-700 dark:text-emerald-200",
  },
  "daniel-revelation": {
    color: "from-orange-600 to-indigo-500",
    chipBg: "bg-indigo-50 dark:bg-indigo-500/10",
    chipText: "text-indigo-700 dark:text-indigo-200",
  },
  health: {
    color: "from-orange-600 to-cyan-500",
    chipBg: "bg-cyan-50 dark:bg-cyan-500/10",
    chipText: "text-cyan-700 dark:text-cyan-200",
  },
  "focus-family": {
    color: "from-orange-600 to-rose-500",
    chipBg: "bg-rose-50 dark:bg-rose-500/10",
    chipText: "text-rose-700 dark:text-rose-200",
  },
  family: {
    color: "from-orange-600 to-rose-500",
    chipBg: "bg-rose-50 dark:bg-rose-500/10",
    chipText: "text-rose-700 dark:text-rose-200",
  },
  default: {
    color: "from-orange-600 to-slate-600",
    chipBg: "bg-slate-50 dark:bg-slate-500/10",
    chipText: "text-slate-700 dark:text-slate-200",
  },
};

// Local fallback (all available)
const FALLBACK = [
  {
    id: "discover",
    slug: "discover",
    title: "Discover",
    description: "A 26-lesson journey through Bible foundations and prophecy.",
    lessons: 26,
    available: true,
  },
  {
    id: "ugunduzi",
    slug: "ugunduzi",
    title: "Ugunduzi",
    description: "Discover Swahili lessons — 26 topics of faith and discovery.",
    lessons: 26,
    available: true,
  },
  {
    id: "daniel-revelation",
    slug: "daniel-revelation",
    title: "Daniel & Revelation",
    description: "Prophetic study of Daniel & Revelation — 21 deep lessons.",
    lessons: 21,
    available: true,
  },
  {
    id: "health",
    slug: "health",
    title: "Health",
    description: "Bible-based health and wellness education — 20 lessons.",
    lessons: 20,
    available: true,
  },
  {
    id: "focus-family",
    slug: "focus-family",
    title: "Focus on the Family",
    description: "Faith-based family life & relationships — 16 lessons.",
    lessons: 16,
    available: true,
  },
].map((c) => {
  const p = PALETTE[c.slug] || PALETTE.default;
  return { ...c, ...p };
});

function SkeletonCard() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg dark:bg-slate-950/60 dark:border-slate-800">
      <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800 mb-5" />
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="h-14 w-14 rounded-2xl bg-slate-100 dark:bg-slate-900" />
        <div className="h-5 w-24 rounded-lg bg-slate-100 dark:bg-slate-900" />
      </div>
      <div className="h-6 w-2/3 rounded-lg bg-slate-100 dark:bg-slate-900 mb-3" />
      <div className="h-4 w-full rounded-lg bg-slate-100 dark:bg-slate-900 mb-2" />
      <div className="h-4 w-5/6 rounded-lg bg-slate-100 dark:bg-slate-900 mb-5" />
      <div className="h-11 w-full rounded-2xl bg-slate-100 dark:bg-slate-900" />
    </div>
  );
}

export default function Courses() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setLoadError("");

      try {
        const apiCourses = await fetchCourses();

        // Deduplicate by slug/id
        const deduped = [
          ...new Map(
            (Array.isArray(apiCourses) ? apiCourses : []).map((c) => [
              String(c.slug || c.id || c.course_slug || ""),
              c,
            ])
          ).values(),
        ].filter((c) => String(c.slug || c.id || "").trim().length > 0);

        const normalized = deduped.map((c) => {
          const slug = String(c.slug || c.id || c.course_slug || "").trim();
          const palette = PALETTE[slug] || PALETTE.default;

          return {
            id: slug, // keep consistent key
            slug,
            title: c.title || slug,
            description: c.description || "",
            lessons: Number(c.total_lessons || c.lessons || c.lessonCount || 0) || 0,
            available: true, // always available
            ...palette,
          };
        });

        if (mounted) setCourses(normalized.length ? normalized : FALLBACK);
      } catch (e) {
        if (mounted) {
          setCourses(FALLBACK);
          setLoadError(
            e?.message ||
              "Could not load courses. Ensure backend CORS includes your frontend URL and restart the server."
          );
        }
      } finally {
        mounted && setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredSorted = useMemo(() => {
    const list = Array.isArray(courses) ? courses : [];
    const needle = String(q || "").trim().toLowerCase();

    const filtered = needle
      ? list.filter((c) => {
          const hay = `${c.title || ""} ${c.description || ""} ${c.slug || ""}`.toLowerCase();
          return hay.includes(needle);
        })
      : list;

    return filtered.slice().sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }, [courses, q]);

  const handleOpenCourse = (courseIdOrSlug) => {
    navigate(`/courses/${encodeURIComponent(courseIdOrSlug)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <Navbar />

      {/* HERO */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-amber-600" />
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-white/15 blur-3xl" />

        <div className="relative pt-24 pb-10 sm:pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <h1 className="text-2xl sm:text-4xl font-extrabold text-white">
                Explore Courses
              </h1>
              <p className="mt-2 text-white/90 text-base sm:text-lg">
                Structured, Bible-centered learning — continue anytime, on any device.
              </p>

              {/* Search */}
              <div className="mt-6">
                <div className="flex items-center gap-3 rounded-2xl bg-white/15 border border-white/20 px-4 py-3 backdrop-blur">
                  <Search className="text-white/80" size={18} />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search courses…"
                    className="w-full bg-transparent outline-none text-white placeholder:text-white/70"
                    aria-label="Search courses"
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-white/85">
                  <span className="inline-flex items-center gap-2 rounded-2xl bg-white/15 border border-white/20 px-3 py-1.5 font-semibold">
                    {loading ? "Loading…" : `${filteredSorted.length} course(s)`}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-2xl bg-white/15 border border-white/20 px-3 py-1.5 font-semibold">
                    <CheckCircle2 size={16} />
                    All available
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error banner (non-blocking) */}
      {loadError && (
        <div className="px-4 sm:px-6 lg:px-8 mt-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start gap-2 text-sm rounded-2xl border border-yellow-200 bg-yellow-50 text-yellow-800 px-4 py-3 dark:bg-yellow-500/10 dark:border-yellow-500/30 dark:text-yellow-200">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <div className="min-w-0">{loadError}</div>
            </div>
          </div>
        </div>
      )}

      {/* MAIN */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-10">
        <div className="max-w-7xl mx-auto">
          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : filteredSorted.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSorted.map((course) => {
                const lessonsLabel = course.lessons ? `${course.lessons} lessons` : "Lessons";
                const palette = PALETTE[course.slug] || PALETTE.default;

                return (
                  <button
                    key={course.slug}
                    onClick={() => handleOpenCourse(course.slug)}
                    className="text-left group relative rounded-3xl border border-slate-200 bg-white p-6 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all dark:bg-slate-950/60 dark:border-slate-800"
                  >
                    {/* Top gradient accent */}
                    <div
                      className={`absolute left-0 top-0 h-2 w-full rounded-t-3xl bg-gradient-to-r ${palette.color}`}
                      aria-hidden
                    />

                    {/* Availability chip */}
                    <div className="absolute top-5 right-5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-extrabold dark:bg-emerald-500/10 dark:text-emerald-200">
                        <CheckCircle2 size={14} /> Available
                      </span>
                    </div>

                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div
                        className={`h-14 w-14 rounded-2xl flex items-center justify-center border border-slate-200 bg-white shadow-sm dark:bg-slate-900 dark:border-slate-700`}
                      >
                        <div className={`h-10 w-10 rounded-2xl bg-gradient-to-br ${palette.color} flex items-center justify-center`}>
                          <BookOpen className="text-white" size={20} />
                        </div>
                      </div>

                      <span
                        className={`inline-flex items-center rounded-2xl px-3 py-1.5 text-xs font-extrabold border border-slate-200 bg-white text-slate-700 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200`}
                      >
                        {lessonsLabel}
                      </span>
                    </div>

                    <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 group-hover:text-orange-700 transition-colors dark:text-white dark:group-hover:text-orange-200">
                      {course.title}
                    </h3>

                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      {course.description || "—"}
                    </p>

                    <div className="mt-5 flex items-center justify-between">
                      <span
                        className={`inline-flex items-center gap-2 rounded-2xl px-3 py-1.5 text-xs font-extrabold ${palette.chipBg} ${palette.chipText} border border-slate-200/60 dark:border-slate-700/60`}
                      >
                        <Clock size={14} />
                        Self-paced
                      </span>

                      <span className="inline-flex items-center gap-2 text-sm font-extrabold text-slate-900 dark:text-white">
                        Open course <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>

                    {/* Subtle hover wash */}
                    <div
                      className={`pointer-events-none absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-[0.06] transition-opacity bg-gradient-to-br ${palette.color}`}
                      aria-hidden
                    />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-lg dark:bg-slate-950/60 dark:border-slate-800">
              <p className="text-slate-900 dark:text-white font-extrabold text-lg">
                No courses found.
              </p>
              <p className="mt-2 text-slate-600 dark:text-slate-300">
                Try a different search term.
              </p>
              <div className="mt-5">
                <button
                  onClick={() => setQ("")}
                  className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                >
                  Clear search
                </button>
              </div>
            </div>
          )}

          {/* Premium CTA band */}
          <div className="mt-10 rounded-3xl border border-slate-200 bg-white/80 backdrop-blur shadow-lg p-6 dark:bg-slate-950/60 dark:border-slate-800">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white">
                  Happy learning 👏
                </h3>
                <p className="text-slate-600 dark:text-slate-300 mt-1">
                  Pick a course, follow the lessons, and track your progress across devices.
                </p>
              </div>

              <div className="flex items-stretch sm:items-center gap-3">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 font-extrabold text-slate-900 hover:bg-slate-50 transition dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800"
                >
                  Back to dashboard
                </Link>

                <Link
                  to="/certificates"
                  className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                >
                  View certificates
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ✅ Shared footer */}
      <Footer />
    </div>
  );
}
